import moment from 'moment';
import _ from 'lodash';
import formatCurrency from 'format-currency';

class PaylocityPayroll {
  constructor($rootScope, $state, $http, $interval, $scope, $uibModal, toastr) {
    "ngInject";
    this.state = $state;
    this.$http = $http;
    this.$rootScope = $rootScope;
    this.$interval = $interval;
    this.$scope = $scope;
    this.$uibModal = $uibModal;
    this.toastr = toastr;

    this.importProgress = {
      inProgress: false,
      total: 0,
      completed: 0,
      startTimeEpochMillis: null,
      message: ''
    };
    this._importPollPromise = null;
    this._userStartedPayrollImport = false;
    this._suppressHydrateApply = false;
    this.copyToTimeclerkInProgress = false;
    this.paylocityIntegration = { payrollSyncEnabled: false };
    this.payrollTableFilterRefresh = false;
    this.reimportingPayrollKey = null;
    this.copyingToTimeclerkPayrollKey = null;

    this.pendingNotifier = null;
    this.notificationRecipients = {
      manualImportNotificationUserIds: [],
      importCompletionNotificationUserIds: [],
      copyToTimeclerkNotificationUserIds: []
    };
    this.availableNotificationUsers = {
      manualImportNotificationUserIds: [],
      importCompletionNotificationUserIds: [],
      copyToTimeclerkNotificationUserIds: []
    };
    this.scheduleHours = _.range(0, 24);
    this.scheduleMinutes = [0, 15, 30, 45];
    this.syncSettings = this._defaultSyncSettings();

    let self = this;
    window.paylocityPayrollCtrl = self;

    $scope.$on('$destroy', function () {
      if (window.paylocityPayrollCtrl === self) {
        delete window.paylocityPayrollCtrl;
      }
      self._suppressHydrateApply = false;
      if (self._importPollPromise) {
        $interval.cancel(self._importPollPromise);
        self._importPollPromise = null;
      }
    });

    $scope.$watch(function () {
      return self.importProgress.inProgress;
    }, function (newVal, oldVal) {
      if (newVal !== oldVal) {
        self._refreshPayrollTable();
      }
    });

    $scope.$watch(function () {
      return self.reimportingPayrollKey;
    }, function (newVal, oldVal) {
      if (newVal !== oldVal) {
        self._refreshPayrollTable();
      }
    });

    $scope.$watch(function () {
      return self.copyingToTimeclerkPayrollKey;
    }, function (newVal, oldVal) {
      if (newVal !== oldVal) {
        self._refreshPayrollTable();
      }
    });

    this.payrollColumns = [
      {
        displayName: 'Check Date',
        value: 'checkDate',
        sortable: true,
        dataType: 'date',
        formatting: function (record) {
          return '<span class="text-info" style="cursor: pointer; text-decoration: underline;">' + (record.checkDate || '') + '</span>';
        },
        clickFunction: function (record) {
          if (!record.checkDate) return;
          self.state.go('paylocity-payroll-details', {
            checkDate: record.checkDate
          });
        }
      },
      {
        displayName: 'Pay Period Start',
        value: 'payPeriodStart',
        sortable: true,
        dataType: 'date'
      },
      {
        displayName: 'Pay Period End',
        value: 'payPeriodEnd',
        sortable: true,
        dataType: 'date'
      },
      {
        displayName: 'Employees Paid',
        value: 'employeesPaid',
        sortable: true,
        dataType: 'number'
      },
      {
        displayName: 'Number of Checks',
        value: 'checks',
        sortable: true,
        dataType: 'number'
      },
      {
        displayName: 'Gross Paid',
        value: 'totalGross',
        sortable: true,
        dataType: 'text',
        formatting: function (record) {
          return '$' + formatCurrency(record.totalGross);
        }
      },
      {
        displayName: 'Net Paid',
        value: 'totalNet',
        sortable: true,
        dataType: 'text',
        formatting: function (record) {
          return '$' + formatCurrency(record.totalNet);
        }
      },
      {
        displayName: 'Tax Witholdings',
        value: 'taxWitholdings',
        sortable: true,
        dataType: 'number',
        formatting: function (record) {
          return '$' + formatCurrency(record.taxWitholdings || 0);
        }
      },
      {
        displayName: 'Deductions',
        value: 'deductions',
        sortable: true,
        dataType: 'date',
        formatting: function (record) {
          return '$' + formatCurrency(record.deductions || 0);
        }
      },
      {
        displayName: 'Last Imported',
        value: 'importedOn',
        sortable: true,
        dataType: 'number'
      },
      {
        displayName: 'Actions',
        value: 'actions',
        dataType: 'text',
        sortable: false,
        formatting: function (record) {
          var idOrCheckDate = record.checkDate || record.id;
          if (!idOrCheckDate) {
            return '';
          }

          var key = String(idOrCheckDate).replace(/'/g, "\\'");
          var disabled = (self.importProgress && self.importProgress.inProgress) ||
            !!self.reimportingPayrollKey ||
            !!self.copyingToTimeclerkPayrollKey ||
            self.copyToTimeclerkInProgress;
          var isReimportLoading = self.reimportingPayrollKey === String(idOrCheckDate);
          var isCopyLoading = self.copyingToTimeclerkPayrollKey === String(idOrCheckDate);

          return '<button type="button" class="btn btn-xs btn-info' + (disabled ? ' disabled' : '') + '"' +
            (disabled ? ' disabled="disabled"' : '') +
            ' onclick="event.stopPropagation(); window.paylocityPayrollCtrl.reimportPayroll(\'' + key + '\')"' +
            ' title="Re-import this pay period from Paylocity">' +
            '<i class="fa fa-sync' + (isReimportLoading ? ' fa-spin' : '') + '"></i> Re-import</button>' +
            '<button type="button" class="btn btn-xs btn-primary m-l-xs' + (disabled ? ' disabled' : '') + '"' +
            (disabled ? ' disabled="disabled"' : '') +
            ' onclick="event.stopPropagation(); window.paylocityPayrollCtrl.copyPayrollToTimeclerkById(\'' + key + '\')"' +
            ' title="Copy this pay period into Timeclerk payroll tables">' +
            '<i class="fa fa-exchange' + (isCopyLoading ? ' fa-spin' : '') + '"></i> Copy to Timeclerk</button>';
        }
      }
    ];

    this.$http.get($rootScope.apiHost + "/api/admin/users/", {withCredentials: true}).then(function (success) {
      self.employees = success.data;
      self._prepareEmployeesForDropdown();
      self._updateNotificationLists();
    });

    this.$http.get($rootScope.apiHost + "/api/integrations/paylocity/status", {withCredentials: true}).then(function (success) {
      self.paylocityIntegration = success.data;
      if (self.paylocityIntegration.payrollSyncEnabled === undefined) {
        self.paylocityIntegration.payrollSyncEnabled = false;
      }
    });

    self._loadSyncSettings();
    self._hydrateImportProgressOnLoad();
  }

  _defaultSyncSettings() {
    return {
      scheduleEnabled: false,
      scheduleHour: 2,
      scheduleMinute: 0,
      scheduleTimeZone: 'America/New_York',
      notifyOnManualImport: true,
      notifyOnImportComplete: true,
      notifyOnCopyToTimeclerkComplete: true,
      manualImportNotificationUserIds: [],
      importCompletionNotificationUserIds: [],
      copyToTimeclerkNotificationUserIds: [],
      lastScheduledRun: null,
      nextScheduledRun: null
    };
  }

  _applySyncSettingsResponse(data) {
    const defaults = this._defaultSyncSettings();
    const settings = _.extend({}, defaults, data || {});
    settings.manualImportNotificationUserIds = settings.manualImportNotificationUserIds || [];
    settings.importCompletionNotificationUserIds = settings.importCompletionNotificationUserIds || [];
    settings.copyToTimeclerkNotificationUserIds = settings.copyToTimeclerkNotificationUserIds || [];
    this.syncSettings = settings;
    this._updateNotificationLists();
  }

  _updateNotificationLists() {
    this._updateNotificationListForField('manualImportNotificationUserIds');
    this._updateNotificationListForField('importCompletionNotificationUserIds');
    this._updateNotificationListForField('copyToTimeclerkNotificationUserIds');
  }

  _prepareEmployeesForDropdown() {
    (this.employees || []).forEach(function (emp) {
      if (emp && !emp.name) {
        emp.name = (emp.firstName || '') + ' ' + (emp.lastName || '');
      }
    });
  }

  _updateNotificationListForField(fieldName) {
    const ids = (this.syncSettings && this.syncSettings[fieldName]) || [];

    this.notificationRecipients[fieldName] = ids.map((userId) => ({
      id: userId,
      name: this.getEmployeeName(userId)
    }));

    const selectedIds = {};
    ids.forEach(function (id) {
      selectedIds[id] = true;
    });

    this.availableNotificationUsers[fieldName] = (this.employees || []).filter(function (emp) {
      return emp && emp.id != null && !selectedIds[emp.id];
    });
  }

  getEmployeeName(userId) {
    const emp = _.find(this.employees, { id: userId });
    if (!emp) {
      return 'User #' + userId;
    }
    return emp.name || ((emp.firstName || '') + ' ' + (emp.lastName || '')).trim();
  }

  _loadSyncSettings() {
    let self = this;
    this.$http.get(this.$rootScope.apiHost + "/api/integrations/paylocity/payroll/sync/settings", {withCredentials: true}).then(function (res) {
      self._applySyncSettingsResponse(res.data);
    }, function () {
      self._applySyncSettingsResponse(null);
    });
  }

  _buildSyncSettingsPayload() {
    return {
      scheduleEnabled: !!this.syncSettings.scheduleEnabled,
      scheduleHour: parseInt(this.syncSettings.scheduleHour, 10) || 0,
      scheduleMinute: parseInt(this.syncSettings.scheduleMinute, 10) || 0,
      scheduleTimeZone: this.syncSettings.scheduleTimeZone || 'America/New_York',
      notifyOnManualImport: !!this.syncSettings.notifyOnManualImport,
      notifyOnImportComplete: !!this.syncSettings.notifyOnImportComplete,
      notifyOnCopyToTimeclerkComplete: !!this.syncSettings.notifyOnCopyToTimeclerkComplete,
      manualImportNotificationUserIds: (this.syncSettings.manualImportNotificationUserIds || []).slice(),
      importCompletionNotificationUserIds: (this.syncSettings.importCompletionNotificationUserIds || []).slice(),
      copyToTimeclerkNotificationUserIds: (this.syncSettings.copyToTimeclerkNotificationUserIds || []).slice()
    };
  }

  saveSyncSettings(modalInstance) {
    let self = this;
    const payload = this._buildSyncSettingsPayload();

    this.$http.patch(this.$rootScope.apiHost + "/api/integrations/paylocity/payroll/sync/settings", payload, {withCredentials: true}).then(function (res) {
      self._applySyncSettingsResponse(res.data);
      self.toastr.success("Sync schedule and notification settings saved.", "SUCCESS");
      if (modalInstance) {
        modalInstance.close();
      }
    }, function (error) {
      self.toastr.error(error.data && error.data.message ? error.data.message : "Failed to save sync settings.", "ERROR");
    });
  }

  openSyncSettingsModal() {
    let self = this;
    this.pendingNotifier = null;
    this._prepareEmployeesForDropdown();
    this._updateNotificationLists();
    this._loadSyncSettings();

    return this.$uibModal.open({
      size: 'lg',
      template: `
        <div class="modal-header">
          <h4 class="modal-title"><i class="fa fa-clock-o"></i> Sync Schedule &amp; Notifications</h4>
        </div>
        <div class="modal-body">
          <h5 style="margin-top: 0; margin-bottom: 12px;">Scheduled Import</h5>
          <div class="row" style="margin-bottom: 10px;">
            <div class="col-sm-12">
              <label style="font-weight: normal; margin-right: 10px;">Enable daily payroll import:</label>
              <toggle ng-model="$modal.parent.syncSettings.scheduleEnabled" on="On" off="Off" on-class="btn-success" off-class="btn-default" size="btn-xs"></toggle>
            </div>
          </div>
          <div class="row" ng-if="$modal.parent.syncSettings.scheduleEnabled" style="margin-bottom: 10px;">
            <div class="col-sm-4">
              <label class="small text-muted">Hour</label>
              <select class="form-control input-sm" ng-model="$modal.parent.syncSettings.scheduleHour" ng-options="hour for hour in $modal.parent.scheduleHours"></select>
            </div>
            <div class="col-sm-4">
              <label class="small text-muted">Minute</label>
              <select class="form-control input-sm" ng-model="$modal.parent.syncSettings.scheduleMinute" ng-options="minute for minute in $modal.parent.scheduleMinutes"></select>
            </div>
            <div class="col-sm-4">
              <label class="small text-muted">Time zone</label>
              <input type="text" class="form-control input-sm" ng-model="$modal.parent.syncSettings.scheduleTimeZone" placeholder="America/New_York">
            </div>
          </div>
          <p class="text-muted small" ng-if="$modal.parent.syncSettings.scheduleEnabled" style="margin-bottom: 16px;">
            Last run: {{$modal.formatScheduleRun($modal.parent.syncSettings.lastScheduledRun)}} |
            Next run: {{$modal.formatScheduleRun($modal.parent.syncSettings.nextScheduledRun)}}
          </p>

          <h5 style="margin-bottom: 12px;"><i class="fa fa-bell-o"></i> Notifications</h5>

          <div style="margin-bottom: 14px;">
            <label style="font-weight: normal;">
              <input type="checkbox" ng-model="$modal.parent.syncSettings.notifyOnManualImport">
              Notify when payroll import is manually requested
            </label>
            <div ng-if="$modal.parent.syncSettings.notifyOnManualImport" style="margin-top: 8px;">
              <div ng-repeat="recipient in $modal.parent.notificationRecipients.manualImportNotificationUserIds" style="margin-bottom: 4px;">
                <span class="label label-default" style="display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: normal; padding: 5px 8px;">
                  {{recipient.name}}
                  <button type="button" class="btn btn-link btn-xs" style="padding: 0; color: #fff;" ng-click="$modal.parent.removeNotificationRecipient('manualImportNotificationUserIds', recipient.id)">&times;</button>
                </span>
              </div>
              <div style="display: flex; gap: 8px; margin-top: 6px;">
                <div style="flex: 1;">
                  <employee-dropdown
                    ng-model="$modal.parent.pendingNotifier"
                    employees="$modal.parent.availableNotificationUsers.manualImportNotificationUserIds"
                    placeholder="Add TimeClerk user...">
                  </employee-dropdown>
                </div>
                <button class="btn btn-xs btn-default" ng-click="$modal.parent.addNotificationRecipient('manualImportNotificationUserIds')" ng-disabled="!$modal.parent.pendingNotifier">Add</button>
              </div>
            </div>
          </div>

          <div style="margin-bottom: 14px;">
            <label style="font-weight: normal;">
              <input type="checkbox" ng-model="$modal.parent.syncSettings.notifyOnImportComplete">
              Notify when payroll import completes
            </label>
            <div ng-if="$modal.parent.syncSettings.notifyOnImportComplete" style="margin-top: 8px;">
              <div ng-repeat="recipient in $modal.parent.notificationRecipients.importCompletionNotificationUserIds" style="margin-bottom: 4px;">
                <span class="label label-default" style="display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: normal; padding: 5px 8px;">
                  {{recipient.name}}
                  <button type="button" class="btn btn-link btn-xs" style="padding: 0; color: #fff;" ng-click="$modal.parent.removeNotificationRecipient('importCompletionNotificationUserIds', recipient.id)">&times;</button>
                </span>
              </div>
              <div style="display: flex; gap: 8px; margin-top: 6px;">
                <div style="flex: 1;">
                  <employee-dropdown
                    ng-model="$modal.parent.pendingNotifier"
                    employees="$modal.parent.availableNotificationUsers.importCompletionNotificationUserIds"
                    placeholder="Add TimeClerk user...">
                  </employee-dropdown>
                </div>
                <button class="btn btn-xs btn-default" ng-click="$modal.parent.addNotificationRecipient('importCompletionNotificationUserIds')" ng-disabled="!$modal.parent.pendingNotifier">Add</button>
              </div>
            </div>
          </div>

          <div style="margin-bottom: 0;">
            <label style="font-weight: normal;">
              <input type="checkbox" ng-model="$modal.parent.syncSettings.notifyOnCopyToTimeclerkComplete">
              Notify when payroll is copied to TimeClerk
            </label>
            <div ng-if="$modal.parent.syncSettings.notifyOnCopyToTimeclerkComplete" style="margin-top: 8px;">
              <div ng-repeat="recipient in $modal.parent.notificationRecipients.copyToTimeclerkNotificationUserIds" style="margin-bottom: 4px;">
                <span class="label label-default" style="display: inline-flex; align-items: center; gap: 6px; font-size: 12px; font-weight: normal; padding: 5px 8px;">
                  {{recipient.name}}
                  <button type="button" class="btn btn-link btn-xs" style="padding: 0; color: #fff;" ng-click="$modal.parent.removeNotificationRecipient('copyToTimeclerkNotificationUserIds', recipient.id)">&times;</button>
                </span>
              </div>
              <div style="display: flex; gap: 8px; margin-top: 6px;">
                <div style="flex: 1;">
                  <employee-dropdown
                    ng-model="$modal.parent.pendingNotifier"
                    employees="$modal.parent.availableNotificationUsers.copyToTimeclerkNotificationUserIds"
                    placeholder="Add TimeClerk user...">
                  </employee-dropdown>
                </div>
                <button class="btn btn-xs btn-default" ng-click="$modal.parent.addNotificationRecipient('copyToTimeclerkNotificationUserIds')" ng-disabled="!$modal.parent.pendingNotifier">Add</button>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-default" ng-click="$modal.close()">Cancel</button>
          <button type="button" class="btn btn-primary" ng-click="$modal.save()">
            <i class="fa fa-save"></i> Save
          </button>
        </div>
      `,
      controller: ['$uibModalInstance', 'parentCtrl', function ($uibModalInstance, parentCtrl) {
        this.parent = parentCtrl;
        this.formatScheduleRun = function (timestamp) {
          return parentCtrl.formatScheduleRun(timestamp);
        };
        this.close = function () {
          $uibModalInstance.dismiss();
        };
        this.save = function () {
          parentCtrl.saveSyncSettings($uibModalInstance);
        };
      }],
      controllerAs: '$modal',
      resolve: {
        parentCtrl: function () {
          return self;
        }
      }
    });
  }

  addNotificationRecipient(fieldName) {
    if (!this.pendingNotifier || !this.pendingNotifier.id) {
      return;
    }

    const ids = (this.syncSettings[fieldName] || []).slice();
    if (ids.indexOf(this.pendingNotifier.id) === -1) {
      ids.push(this.pendingNotifier.id);
      this.syncSettings[fieldName] = ids;
    }
    this.pendingNotifier = null;
    this._updateNotificationListForField(fieldName);
  }

  removeNotificationRecipient(fieldName, userId) {
    this.syncSettings[fieldName] = (this.syncSettings[fieldName] || []).filter(function (id) {
      return id !== userId;
    });
    this._updateNotificationListForField(fieldName);
  }

  formatScheduleRun(timestamp) {
    if (!timestamp) {
      return 'Not scheduled';
    }
    return moment(timestamp).format('MMM DD, YYYY h:mm A');
  }

  togglePayrollSync() {
    let self = this;
    let obj = {
      payrollSyncEnabled: this.paylocityIntegration.payrollSyncEnabled
    };

    this.$http.patch(this.$rootScope.apiHost + "/api/integrations/paylocity", obj, {withCredentials: true}).then(function () {
      self.toastr.success("Payroll synchronization " + (self.paylocityIntegration.payrollSyncEnabled ? "enabled" : "disabled") + ".", "Success");
      self.$http.get(self.$rootScope.apiHost + "/api/integrations/paylocity/status", {withCredentials: true}).then(function (success) {
        self.paylocityIntegration = success.data;
        if (self.paylocityIntegration.payrollSyncEnabled === undefined) {
          self.paylocityIntegration.payrollSyncEnabled = false;
        }
      });
    }, function () {
      self.toastr.error("Could not update payroll synchronization setting.", "ERROR");
      self.paylocityIntegration.payrollSyncEnabled = !self.paylocityIntegration.payrollSyncEnabled;
    });
  }

  _stopImportPolling() {
    if (this._importPollPromise) {
      this.$interval.cancel(this._importPollPromise);
      this._importPollPromise = null;
    }
  }

  _applyImportStatusPayload(status) {
    status = status || {};
    this.importProgress.inProgress = status.inProgress === true;
    this.importProgress.total = status.total || 0;
    this.importProgress.completed = status.completed || 0;
    this.importProgress.startTimeEpochMillis = status.startTimeEpochMillis || null;
    this.importProgress.message = status.message || '';
  }

  _hydrateImportProgressOnLoad() {
    let self = this;
    self.$http.get(self.$rootScope.apiHost + "/api/integrations/paylocity/payroll/import/status", {withCredentials: true}).then(function (res) {
      if (self._suppressHydrateApply) {
        return;
      }
      self._applyImportStatusPayload(res.data);
      if (self.importProgress.inProgress) {
        self._startImportPolling();
      }
    }).catch(function () {
      // Leave defaults; user can retry import.
    });
  }

  _refreshAfterImport() {
    let self = this;
    self.$http.get(self.$rootScope.apiHost + "/api/integrations/paylocity/status", {withCredentials: true}).then(function (res) {
      self.paylocityIntegration = res.data;
      if (self.paylocityIntegration.payrollSyncEnabled === undefined) {
        self.paylocityIntegration.payrollSyncEnabled = false;
      }
    });
  }

  _pollImportStatus() {
    let self = this;
    self.$http.get(self.$rootScope.apiHost + "/api/integrations/paylocity/payroll/import/status", {withCredentials: true}).then(function (res) {
      self._applyImportStatusPayload(res.data);

      if (!self.importProgress.inProgress) {
        self._stopImportPolling();
        self._suppressHydrateApply = false;
        self._refreshAfterImport();
        if (self._userStartedPayrollImport) {
          self._userStartedPayrollImport = false;
          self.toastr.success("Payroll import completed.", "Success");
        }
      }
    }).catch(function () {
      self._stopImportPolling();
      self._userStartedPayrollImport = false;
      self._suppressHydrateApply = false;
      self.importProgress.inProgress = false;
      self.toastr.error("Could not get import status.", "ERROR");
    });
  }

  _startImportPolling() {
    let self = this;
    if (self._importPollPromise) {
      return;
    }
    self._pollImportStatus();
    self._importPollPromise = self.$interval(function () {
      self._pollImportStatus();
    }, 1500);
  }

  syncPayrollData() {
    let self = this;
    if (self.importProgress.inProgress) return;
    self._suppressHydrateApply = true;
    self.importProgress.inProgress = true;
    self.importProgress.message = 'Starting import...';
    this.$http.get(this.$rootScope.apiHost + "/api/integrations/paylocity/payroll/import", {withCredentials: true}).then(function (res) {
      self._userStartedPayrollImport = true;
      self.importProgress.message = (res.data && res.data.message) || 'Payroll import started.';
      self._startImportPolling();
    }, function () {
      self.importProgress.inProgress = false;
      self._userStartedPayrollImport = false;
      self._suppressHydrateApply = false;
      self.toastr.error("Problem starting payroll import.", "ERROR");
    });
  }

  copyPayrollToTimeclerk() {
    let self = this;
    if (self.copyToTimeclerkInProgress || self.reimportingPayrollKey || self.copyingToTimeclerkPayrollKey) {
      return;
    }
    self.copyToTimeclerkInProgress = true;
    function done() {
      self.copyToTimeclerkInProgress = false;
      self._refreshPayrollTable();
    }
    self.$http
      .post(self.$rootScope.apiHost + "/api/integrations/paylocity/payroll/copy-to-timeclerk", {}, {withCredentials: true})
      .then(function () {
        self.toastr.success("Payroll copied to Timeclerk.", "Success");
        done();
      }, function () {
        self.toastr.error("Could not copy payroll to Timeclerk.", "ERROR");
        done();
      });
  }

  _refreshPayrollTable() {
    this.payrollTableFilterRefresh = true;
  }

  reimportPayroll(idOrCheckDate) {
    const self = this;
    if (!idOrCheckDate || self.importProgress.inProgress || self.reimportingPayrollKey ||
        self.copyingToTimeclerkPayrollKey || self.copyToTimeclerkInProgress) {
      return;
    }

    self.reimportingPayrollKey = String(idOrCheckDate);
    self.$http.post(
      self.$rootScope.apiHost + '/api/integrations/paylocity/payroll/' + encodeURIComponent(idOrCheckDate) + '/import',
      {},
      { withCredentials: true }
    ).then(function () {
      self.toastr.success('Payroll re-imported successfully.', 'Success');
      self._refreshPayrollTable();
    }, function (error) {
      const message = error.data && error.data.message
        ? error.data.message
        : 'Could not re-import payroll.';
      self.toastr.error(message, 'ERROR');
    }).finally(function () {
      self.reimportingPayrollKey = null;
      self._refreshPayrollTable();
    });
  }

  copyPayrollToTimeclerkById(idOrCheckDate) {
    const self = this;
    if (!idOrCheckDate || self.importProgress.inProgress || self.reimportingPayrollKey ||
        self.copyingToTimeclerkPayrollKey || self.copyToTimeclerkInProgress) {
      return;
    }

    self.copyingToTimeclerkPayrollKey = String(idOrCheckDate);
    self.$http.post(
      self.$rootScope.apiHost + '/api/integrations/paylocity/payroll/' + encodeURIComponent(idOrCheckDate) + '/copy-to-timeclerk',
      {},
      { withCredentials: true }
    ).then(function () {
      self.toastr.success('Pay period copied to Timeclerk.', 'Success');
      self._refreshPayrollTable();
    }, function (error) {
      const message = error.data && error.data.message
        ? error.data.message
        : 'Could not copy payroll to Timeclerk.';
      self.toastr.error(message, 'ERROR');
    }).finally(function () {
      self.copyingToTimeclerkPayrollKey = null;
      self._refreshPayrollTable();
    });
  }

}

export default PaylocityPayroll;



// WEBPACK FOOTER //
// ./client/pages/admin/integrations/paylocity/capabilities/payroll/paylocity-payroll.controller.js